import * as React from 'react';

import {TPlace, TPoi, TFilter} from '../Definitions';
import {PLACE_DETAILS_FIELDS} from '../Config/maps';
import {without, intersection} from 'lodash';

// Alias
type TPlacesService = google.maps.places.PlacesService;
type TStatus = google.maps.places.PlacesServiceStatus;
type TPagination = google.maps.places.PlaceSearchPagination;
type TBounds = google.maps.LatLngBounds;

const getValidQueryBounds: (r: TBounds, c: TBounds) => TBounds = (reference, check) => {
	const noIntersection = !reference.intersects(check);
	const fullyIncluded = check.contains(reference.getNorthEast())
		&& check.contains(reference.getSouthWest());

	return (noIntersection || fullyIncluded) ? reference : check;
};

type SearchQuery = {
	keyword: string;
	bounds: TBounds;
};

type QueryResult = {
	places: TPlace[]|null;
	timestamp: number;
}
async function loadPlacesForQuery({placesService, query, timestamp}: {
	placesService: TPlacesService;
	query: SearchQuery;
	timestamp: number;
}) {
	let places: TPlace[] = [];

	const onResult = (resolve: (queryResult: QueryResult) => void) => (result: TPlace[], status: TStatus, pagination: TPagination) => {
		if (status === google.maps.places.PlacesServiceStatus.OK) {
			places.push(...result);

			if (pagination.hasNextPage) {
				pagination.nextPage();
				return;
			}

			resolve({places, timestamp});
		} else if (status === google.maps.places.PlacesServiceStatus.OVER_QUERY_LIMIT) {
			resolve({places: null, timestamp});
		}
	};

	return new Promise<QueryResult>(
		resolve => {
			placesService.nearbySearch(
				{
					rankBy: google.maps.places.RankBy.PROMINENCE,
					...query
				},
				onResult(resolve)
			)
		});
};

type DetailResult = {
	place: TPlace|null;
	retry: number;
	msg?: string;
};
async function loadPlace({placesService, placeId, retry = 0, onResolve}: {
	placesService: TPlacesService;
	placeId: string;
	retry?: number;
	onResolve?: Function
}) {
	// Add delay, if there was a Over-Query-Timeout.
	await new Promise(resolve => window.setTimeout(resolve, Math.min(retry * 500, 2000)));

	return new Promise<DetailResult>(resolve => {
		placesService.getDetails({
			placeId,
			fields: PLACE_DETAILS_FIELDS
		}, (place: TPlace, status: TStatus) => {
			let resolver = onResolve || resolve;

			if (status === google.maps.places.PlacesServiceStatus.OK) {
				const isClosed = place.business_status === "CLOSED_PERMANENTLY";
				resolver({
					place: isClosed ? null : place,
					retry,
					msg: isClosed && `"${place.name}" is permanently closed.`
				});
			} else if (status === google.maps.places.PlacesServiceStatus.OVER_QUERY_LIMIT && retry < 4) {
				return loadPlace({placesService, placeId, retry: retry + 1, onResolve: resolver});
			} else {
				resolver({place: null, retry: retry + 1, msg: `Something went wrong. Tried to load ${retry} times.`});
			}
		});
	});
};

const debugMe = (...values: any) => {
	if (window.location.search.includes('debugMe=1')) {
		console.log(...values);
	}
};

type TUseWithGooglePlacesProps = {
	placesService: TPlacesService;
	allPois: TPoi[]; // All pois with higher priority. These are places, offering "Tölzer Wertmarke".
	filteredPois: TPoi[]; // Subset of allPois. Pois, matching preselected category or other filter.
	fallbackKeywords: string[]; // A list of category-keywords.
	filter: TFilter;
	mostOuterBounds?: TBounds; // Don't render Places outside these Map-bounds.
};
export default ({placesService, allPois, filteredPois, fallbackKeywords, filter, mostOuterBounds}: TUseWithGooglePlacesProps) => {
	const DEFAULT_KEYWORDS = React.useMemo<string>(() => ['83646','Bad Tölz', ...fallbackKeywords].join('|'), [fallbackKeywords]);
	const [additionalFilter, setAdditionalFilter] = React.useState<TFilter>(filter);
	const poiIds = React.useMemo<string[]>(() => filteredPois.filter(poi =>
		!poi.isBlacklisted
		&& (additionalFilter.hasWertkarte ? poi.hasWertkarte : true)
		&& (additionalFilter.isFairTrade ? poi.isFairTrade : true)
	).map(poi => poi.placeId), [additionalFilter]);
	const allPoiIds = React.useMemo<string[]>(() => allPois.filter(poi => !poi.isBlacklisted).map(poi => poi.placeId), []);
	const blacklistedPoiIds = React.useMemo<string[]>(() => allPois.filter(poi => poi.isBlacklisted).map(poi => poi.placeId), []);

	const timestampLatestQueryLoad = React.useRef<number>(0);
	const timestampLatestDetailLoads = React.useRef<number>(0);

	const failedPlaceIds = React.useRef<string[]>([]);

	const [placesWithDetails, setPlacesWithDetails] = React.useState<{[placeId: string]: TPlace}>({});

	const [query, setQuery] = React.useState<Partial<SearchQuery>>({keyword: DEFAULT_KEYWORDS,});
	const [queryHasBeenRequested, setQueryHasBeenRequested] = React.useState<boolean>(false);
	const [isLoadingQueryResults, setIsLoadingQueryResults] = React.useState<boolean>(false);
	const [isLoadingDetailResults, setIsLoadingDetailResults] = React.useState<boolean>(false);

	const [countResult, setCountResult] = React.useState<number>(0);
	const [resultIds, setResultIds] = React.useState<string[][]>([[], []]);
	const [result, setResult] = React.useState<TPlace[]>([]);

	React.useEffect(() => {
		if (resultIds[0].length && resultIds[1].length) {
			setIsLoadingDetailResults(true);
			timestampLatestDetailLoads.current = Date.now();

			(async () => {
				let timestamp = timestampLatestDetailLoads.current;
				let places: {[placeId: string]: TPlace} = {};

				debugMe(`load details ${resultIds[0].length} of ${resultIds[1].length}`);
				for (const placeId of resultIds[0]) {
					const placeWithDetail = placesWithDetails[placeId];

					if (placeWithDetail === undefined) {
						const {place, msg} = await loadPlace(({placesService, placeId}));

						if (place === null) {
							console.warn(msg, `(ID: ${placeId})`);
							failedPlaceIds.current = failedPlaceIds.current.concat(placeId);

						} else {
							places[placeId] = {
								poi: allPois.find(poi => poi.placeId === place.place_id),
								...place,
							};
						}

						// Don't load further details, if there was another request in the meantime...
						if (timestamp !== timestampLatestDetailLoads.current) {
							break;
						}
					}
				}

				setIsLoadingDetailResults(false);

				// ... but store all fetched places so far.
				setPlacesWithDetails(placesWithDetails => ({
					...placesWithDetails,
					...places
				}));
			})();
		}
	}, [resultIds, countResult]);

	React.useEffect(() => {
		// Check, if there is something to do.
		if (resultIds[0].length && Object.values(placesWithDetails).length) {
			// If we have failed placeIds, we have to remove them and do an new roundtrip.
			// By doing this, we ensure, that there always will be same amount of new results.
			if (intersection(resultIds[0], failedPlaceIds.current).length > 0) {
				debugMe('fill up empty results');
				setResultIds(([current, all]) => {
					const cleanedAll = without(all, ...failedPlaceIds.current);
					return [...[cleanedAll.slice(0, current.length), cleanedAll]];
				});
			} else if (!(queryHasBeenRequested && isLoadingQueryResults) || resultIds[0].length >= countResult) {
				const result = resultIds[0].slice(0, countResult).map(id => placesWithDetails[id]);

				if (result.every(Boolean)) {
					setResult(result);
				}
			}
		}
	}, [resultIds, placesWithDetails, queryHasBeenRequested, isLoadingQueryResults]);

	const isAllowedToLoadMore = (result.length === countResult) && (resultIds[0].length < Math.min(countResult + 9, resultIds[1].length));
	const isAllowedToLoadForQuery =
		countResult > 0
		&& query.bounds
		&& (query.keyword !== DEFAULT_KEYWORDS || !(additionalFilter.hasWertkarte || additionalFilter.isFairTrade))
		&& (countResult + 9) > resultIds[1].length
		&& !queryHasBeenRequested;

	// Load more results.
	React.useEffect(() => {
		if (isAllowedToLoadMore) {
			debugMe('loadMore');
			setResultIds(([_, all]) => [all.slice(0, countResult + 9), all]);
		}
	}, [isAllowedToLoadMore, setResultIds]);

	React.useEffect(() => {
		debugMe('reset');
		setIsLoadingQueryResults(false);
		setResult([]);

		if (query.keyword === DEFAULT_KEYWORDS) {
			setResultIds([poiIds.slice(0, 8), poiIds]);
		} else {
			setResultIds([[], []]);
		}

		setCountResult(8);
		setQueryHasBeenRequested(false);
	}, [query.keyword, poiIds]);

	// Fetch more places by query.
	React.useEffect(() => {
		if (isAllowedToLoadForQuery) {
			debugMe('fetch for query', result.length, countResult, `Bad Tölz+${query.keyword}`);
			timestampLatestQueryLoad.current = Date.now();

			setIsLoadingQueryResults(true);
			setQueryHasBeenRequested(true);

			(async () => {
				const {places, timestamp}:QueryResult = await loadPlacesForQuery({
					placesService,
					query: {
						bounds: query.bounds,
						keyword: `Bad Tölz+${query.keyword}`,
					} as SearchQuery,
					timestamp: timestampLatestQueryLoad.current!
				});

				if (timestamp === timestampLatestQueryLoad.current) {
					if (places === null) {
						debugMe('reset query-fetch');
						window.setTimeout(() => {
							setIsLoadingQueryResults(false);
							setQueryHasBeenRequested(false);
						}, 500);
					} else {
						debugMe('succeeded query-fetch', places.length, places.map(({name}) => name).join(', '));
						const hasQuery = query.keyword !== DEFAULT_KEYWORDS;
						const intersectionBounds = getValidQueryBounds(query.bounds as TBounds, mostOuterBounds as TBounds);
						let queryResultIds = places
							.filter(place => place.geometry?.location instanceof google.maps.LatLng
								? intersectionBounds.contains(place.geometry?.location)
								: false
							)
							.map(place => place.place_id) as string[];

						if (hasQuery) {
							queryResultIds = [...new Set([
								...intersection(queryResultIds, allPoiIds),
							].concat(queryResultIds))];
						}

						setResultIds(([current]) => {
							let updatedOverallResult = [...new Set(without(
								(hasQuery ? [] : poiIds).concat(queryResultIds),
								...failedPlaceIds.current,
								...blacklistedPoiIds
							))];

							if (additionalFilter.hasWertkarte) {
								const allIdsWithWertmarke = allPois
									.filter(poi => poi.hasWertkarte)
									.map(poi => poi.placeId);

								debugMe('filter Wertkarte', allIdsWithWertmarke);
								updatedOverallResult = updatedOverallResult.filter(placeId => allIdsWithWertmarke.includes(placeId));
							}

							if (additionalFilter.isFairTrade) {
								const allIdsAreFairTrade = allPois
									.filter(poi => poi.isFairTrade)
									.map(poi => poi.placeId);

								debugMe('filter isFairTrade', allIdsAreFairTrade);
								updatedOverallResult = updatedOverallResult.filter(placeId => allIdsAreFairTrade.includes(placeId));
							}

							return [
								current.length >= countResult ? current : updatedOverallResult.slice(0, countResult),
								updatedOverallResult
							];
						});
						setIsLoadingQueryResults(false);
					}
				}
			})();
		}
	}, [isAllowedToLoadForQuery, setQueryHasBeenRequested, query, setResultIds, setIsLoadingQueryResults]);

	return {
		onUpdateMapBounds: (bounds: TBounds) => setQuery(query => ({...query, bounds})),
		onUpdateKeyword: (keyword: string) => setQuery(query => ({...query, keyword: keyword || DEFAULT_KEYWORDS})),
		countResult,
		next: () => setCountResult(count => count + 9),
		isLoading: (timestampLatestDetailLoads.current > 0 || timestampLatestQueryLoad.current > 0) && result.length > 0 && result.length < countResult && countResult > 0,
		isLoadingDetailResults,
		isLoadingQueryResults,
		result,
		hasMoreResults: (countResult < resultIds[1].length) || isLoadingQueryResults,
		additionalFilter,
		onUpdateAdditionalFilter: (filterUpdate: Partial<TFilter>) =>
			setAdditionalFilter(currentFilter => ({
				...currentFilter,
				...filterUpdate,
			})),
	};
};
